import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { LoginFacade } from '../../../state/login.facade';
import { ConfigService } from '../../../../services/config.service';
import { UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { IdentifierType, ParameterType, RegisterOption, USER_OF } from '../../../../models/client-configuration';
import { FormBuilderService } from '../../../../services/form-builder.service';
import { LocaleService } from '../../../../services/locale.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { LoginDialogComponent } from '../../login-dialog/login-dialog.component';
import { combineLatest } from 'rxjs';

type SignupCheckbox = {
  id: string,
  state: boolean,
  text: string,
};

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss']
})
export class SignupComponent implements OnInit {
  private readonly loginFacade = inject(LoginFacade);
  private readonly configService = inject(ConfigService);
  private readonly formBuilderService = inject(FormBuilderService);
  private readonly localeService = inject(LocaleService);
  private readonly dialogRef = inject(MatLegacyDialogRef<LoginDialogComponent>);

  readonly privacyPolicyUrl = signal<string>(this.configService.get().privatePolicy);
  readonly termsUrl = signal<string>(this.configService.get().termsUrl);
  readonly customMessage = signal(this.configService.get().registrationMessage);
  readonly checkboxes = signal<SignupCheckbox[]>([]);
  readonly isAnyCheckboxUnchecked = computed(() => this.checkboxes().some((c) => !c.state));
  readonly showValidateRutButton = signal<boolean>(false);
  readonly signupStatus = toSignal(this.loginFacade.signupStatus$);
  readonly lastScreen = toSignal(this.loginFacade.lastScreen$);

  form: UntypedFormGroup = new UntypedFormGroup({});
  formModel: any = {};
  formFields: FormlyFieldConfig[][];
  formOptions: FormlyFormOptions = {};
  selectedFormFields: FormlyFieldConfig[];
  selectedForm = 0;

  constructor() {
    effect(() => {
      if (this.signupStatus() === 'NEEDS_APPROVAL') {
        this.dialogRef.close();
      }
    });
  }

  ngOnInit() {
    this.initializeForm(this.getSignupParameters());
    this.initializeCheckboxes();
  }

  loadLogin(event: MouseEvent) {
    event.preventDefault();
    this.loginFacade.changeScreen('login');
  }

  handleCheckboxCheck({ id }: SignupCheckbox, event: boolean) {
    this.checkboxes.update((checkboxes) => {
      return checkboxes.map((checkbox) => {
        if (id === checkbox.id) {
          return {
            ...checkbox,
            state: event,
          };
        }

        return checkbox;
      });
    });
  }

  signup(event: Event) {
    event.preventDefault();

    const targetLibrary = this.formModel[ParameterType.CODIGO];
    const options = this.configService.get().registerOptions;
    const targetMultiple = options.find((o) => o.multipleId === targetLibrary);
    let biblioCodeDefaultValue = null;

    if (!targetMultiple) {
      const codeField = options[0].fields.find((f) => f.tipoParametro === ParameterType.CODIGO);
      biblioCodeDefaultValue = codeField ? 'BI' + codeField.defaultValue : null;
    }

    this.formModel[ParameterType.CODIGO] = targetMultiple ? targetMultiple.library : biblioCodeDefaultValue;
    let multipleId: number;

    if (targetMultiple) {
      multipleId = targetMultiple.multipleId;
    }

    // Cuando el email no es obligatorio, si se escribe algo en el input y luego se borra, en accountModel se
    // almacena cadena vacía. Añadimos comprobación para evitar que llegue mail con cadena vacía al opac
    if (this.formModel[ParameterType.CORREO_ELECTRONICO] === '') {
      this.formModel[ParameterType.CORREO_ELECTRONICO] = null;
    }

    this.adaptUserOfParameter();

    if (!this.configService.isClientDibam()) {
      this.formBuilderService.adaptMoECParams(this.formModel);
    }

    this.loginFacade.signup(this.formModel, multipleId);
  }

  validateRutCode() {
    if (this.form.invalid) {
      return;
    }

    const rut = this.formModel[ParameterType.IDENTIFICADOR_EXTERNO][0]['data-register'];
    const targetLibrary = this.formModel[ParameterType.CODIGO];
    const options = this.configService.get().registerOptions;
    const targetMultiple = options.find((o) => o.multipleId === targetLibrary);

    this.loginFacade.getRutInfo(rut, targetMultiple.multipleId).subscribe(newOptions => {
      this.selectedForm = 0;
      this.initializeForm(newOptions);
      this.selectedFormFields.forEach((f) => {
        if (f.key === ParameterType.IDENTIFICADOR_EXTERNO.toString()) {
          this.makeFieldReadonly(f.fieldGroup[1]);
          this.formModel[f.key][0] = {
            field: IdentifierType.RUT,
            data: rut,
          };

          return;
        }

        if (f.defaultValue === null || !f.defaultValue.trim() || f.key === ParameterType.CONTRASENNA.toString()) {
          return;
        }

        f.type = 'input';
        this.makeFieldReadonly(f);
        this.obfuscateField(f);
        this.formModel[f.key as string] = f.key === ParameterType.FECHA_NACIMIENTO.toString() ? new Date(f.defaultValue) : f.defaultValue;
      });

        this.showValidateRutButton.set(false);
    });
  }

  private makeFieldReadonly(field: FormlyFieldConfig) {
    field.templateOptions = {
      ...field.templateOptions,
      readonly: true,
    };
  }

  private obfuscateField(field: FormlyFieldConfig) {
    field.templateOptions = {
      ...field.templateOptions,
      type: 'password',
    };
  }

  private initializeForm(parameters: RegisterOption[]) {
    if (!parameters) {
      return;
    }

    this.formFields = this.formBuilderService.parse(parameters, this.configService.isEleo());

    if (this.configService.isSaoPaulo()) {
      this.reorderSaoPauloFields();
      this.eventGetCep();
    }

    this.selectedFormFields = this.formFields[this.selectedForm];
    this.formBuilderService.registerLibrarySelect$.subscribe((value) => {
      this.refreshLibraryParams(parameters, value);
    });
    this.refreshLibraryParams(parameters, {
      value: parameters[0].multipleId,
    });

    this.formBuilderService.genericSelect$.subscribe((e) => {
      this.formBuilderService.populateSelect(parameters, this.formFields, this.selectedForm, e);
    });
  }

  private refreshLibraryParams(parameters: RegisterOption[], { value }: { value: number | string }) {
    this.selectedForm = parameters.findIndex((f) => f.multipleId === value);

    if (this.selectedForm === -1) {
      this.selectedForm = parameters.findIndex((f) => f.library === value);
    }

    this.selectedFormFields = this.formFields[this.selectedForm];
    const registerOption = parameters[this.selectedForm];

    if (this.formBuilderService.shouldLoadRutStep(registerOption.name)) {
      this.selectedFormFields = this.formBuilderService.getRutFirstStepConfig(registerOption);
      this.showValidateRutButton.set(true);
    } else {
      this.showValidateRutButton.set(false);
    }

    const newIdentifierField = this.selectedFormFields.find((f) => f.key === ParameterType.IDENTIFICADOR_EXTERNO.toString());

    if (Object.keys(this.formModel).length === 0) {
      const selectedCode = this.selectedFormFields.find((f) => f.key === ParameterType.CODIGO.toString());

      this.formModel[ParameterType.IDENTIFICADOR_EXTERNO] = [
        { field: newIdentifierField.fieldGroup[0].defaultValue },
      ];
      this.formModel[ParameterType.CODIGO] = selectedCode ? selectedCode.defaultValue.value : '';
    } else {
      this.formModel[ParameterType.IDENTIFICADOR_EXTERNO][0].field = newIdentifierField.fieldGroup[0].defaultValue;
    }

    this.form = new UntypedFormGroup({});
  }

  private getSignupParameters(): RegisterOption[] {
    const options = this.configService.get().registerOptions;
    let parameters: RegisterOption[] = options.map((o) => {
      o.fields = o.fields.filter((f) => f.alta === true);
      return o;
    });

    if (!this.configService.get().multipleSignUp) {
      parameters = [parameters.shift()];
    }

    return parameters;
  }

  /**
   * Changes the order of address, address type and postal code in form
   * and sets address and address type after postal code
   */
  private reorderSaoPauloFields() {
    const tempFields: FormlyFieldConfig[][] = [];
    let addressType = null;
    let address = null;

    for (const field of this.formFields) {
      const tempParameters: FormlyFieldConfig[] = [];

      for (const parameter of field) {
        if (parameter.key === ParameterType.TIPO_DIRECCION.toString()) {
          addressType = parameter;
          continue;
        }

        if (parameter.key === ParameterType.DIRECCION.toString()) {
          address = parameter;
          continue;
        }

        if (parameter.key === ParameterType.CODIGO_POSTAL.toString()) {
          tempParameters.push(parameter);

          if (address !== null) {
            tempParameters.push(address);
          }

          if (addressType !== null) {
            tempParameters.push(addressType);
          }

          continue;
        }

        tempParameters.push(parameter);
      }

      tempFields.push(tempParameters);
    }

    this.formFields = tempFields;
  }

  private eventGetCep() {
    this.formBuilderService.eventGetCep$.subscribe((cep) => {
      this.formBuilderService.setValueSaoPaulo(this.selectedFormFields, this.formModel, cep, this.form);
    });
  }

  private initializeCheckboxes() {
    if (this.configService.isCaf()) {
      this.initializeCafCheckboxes();
    } else {
      this.initializeDefaultCheckboxes();
    }
  }

  private initializeDefaultCheckboxes() {
    combineLatest([
      this.localeService.stream('signup.terms'),
      this.localeService.stream('signup.termsUrl'),
      this.localeService.stream('signup.privacy'),
      this.localeService.stream('signup.privacyPolicy'),
    ]).subscribe(([terms, termsUrlLabel, privacy, privacyPolicyLabel]) => {
      this.checkboxes.set([{
        id: 'defaultTermsConditions',
        state: this.getCheckboxState('defaultTermsConditions'),
        text: `${terms} <a href="${this.termsUrl()}" target="_blank" rel="noopener">${termsUrlLabel}</a> ${privacy} <a href="${this.privacyPolicyUrl()}" target="_blank" rel="noopener"> ${privacyPolicyLabel}</a>`,
      }]);
    });
  }

  private initializeCafCheckboxes() {
    combineLatest([
      this.localeService.stream('signup.caf.internationalRelationship'),
      this.localeService.stream('signup.caf.termsConditions'),
      this.localeService.stream('signup.caf.arbitrationTerms'),
      this.localeService.stream('signup.caf.privacyPolicy'),
    ]).subscribe(([internationalRelationship, termsConditions, arbitrationTerms, privacyPolicy]) => {
      this.checkboxes.set([
        {
          id: 'cafInternationalRelationship',
          state: this.getCheckboxState('cafInternationalRelationship'),
          text: internationalRelationship,
        },
        {
          id: 'cafTermsConditions',
          state: this.getCheckboxState('cafTermsConditions'),
          text: termsConditions,
        },
        {
          id: 'cafArbitrationTerms',
          state: this.getCheckboxState('cafArbitrationTerms'),
          text: arbitrationTerms,
        },
        {
          id: 'cafPrivacyPolicy',
          state: this.getCheckboxState('cafPrivacyPolicy'),
          text: privacyPolicy,
        }
      ]);
    });
  }

  private getCheckboxState(id: string): boolean {
    return this.checkboxes().find((checkbox) => checkbox.id === id)?.state ?? false;
  }

  private adaptUserOfParameter() {
    const userOf = this.formModel[ParameterType.BIBLIOMETRO];

    if (userOf === USER_OF.BIBLIOMETRO) {
      this.formModel[ParameterType.BIBLIOMETRO] = 'S';
    } else {
      delete this.formModel[ParameterType.BIBLIOMETRO];

      if (userOf === USER_OF.AIRPORT) {
        this.formModel[ParameterType.AEROPUERTO] = 'S';
      } else if (userOf === USER_OF.SANTIAGO_LIBRARY) {
        this.formModel[ParameterType.SANTIAGO] = 'S';
      } else if (userOf === USER_OF.MALL_BARRIO) {
        this.formModel[ParameterType.MALLBARRIOINDEPENDENCIA] = 'S';
      }
    }
  }
}
